import React, { useEffect, useMemo, useState } from "react";
import { FaFacebookF, FaInstagram } from "react-icons/fa";
import {
  FiExternalLink,
  FiMail,
  FiMapPin,
  FiPhone,
  FiShield,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

import { getSettings } from "../../../services/settingsServices";

const API_BASE = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";

const DEFAULT_SETTINGS = {
  nom_asso: "MonAppliAsso",
  logo_asso: "",
  adresse_asso: "",
  commune_asso: "",
  tel_asso: "",
  mail_asso: "",
  facebook_asso: "",
  instagram_asso: "",
};

const QUICK_LINKS = [
  { label: "Associations", id: "sections" },
  { label: "Actualités", id: "news" },
  { label: "Événements", id: "events" },
  { label: "Résultats", id: "palmares" },
  { label: "Albums photos", id: "galerie" },
  { label: "Partenaires", id: "partenaires" },
  { label: "Contact", id: "contact" },
];

const LEGAL_LINKS = [
  { label: "Mentions légales", path: "/mentions-legales" },
  { label: "Politique de confidentialité", path: "/politique-confidentialite" },
  { label: "Conditions d’utilisation", path: "/conditions-utilisation" },
];

const isExternalUrl = (url = "") => /^https?:\/\//i.test(String(url || ""));

const toMediaUrl = (url = "") => {
  const value = String(url || "").trim();

  if (!value) return "";

  if (
    isExternalUrl(value) ||
    value.startsWith("data:") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  if (value.startsWith("/uploads")) {
    return `${API_BASE}${value}`;
  }

  return value;
};

const openExternal = (url) => {
  if (!url) return;

  const target = isExternalUrl(url) ? url : `https://${url}`;
  window.open(target, "_blank", "noopener,noreferrer");
};

export default function Footer({ view = "portail" }) {
  const navigate = useNavigate();

  const [settings, setSettings] = useState(DEFAULT_SETTINGS);

  useEffect(() => {
    let mounted = true;

    async function fetchSettings() {
      try {
        const data = await getSettings();
        const s = Array.isArray(data) ? data[0] : data;

        if (!mounted || !s) return;

        setSettings({
          ...DEFAULT_SETTINGS,
          ...s,
        });
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres :", error);
      }
    }

    fetchSettings();

    return () => {
      mounted = false;
    };
  }, []);

  const logoUrl = toMediaUrl(settings.logo_asso);

  const fullAddress = useMemo(() => {
    return [settings.adresse_asso, settings.commune_asso]
      .filter(Boolean)
      .join(", ");
  }, [settings.adresse_asso, settings.commune_asso]);

  const mapUrl = useMemo(() => {
    if (!fullAddress) return "";

    return `https://www.google.com/maps?q=${encodeURIComponent(
      fullAddress,
    )}&output=embed`;
  }, [fullAddress]);

  const handleAnchorNavigate = (event, id) => {
    event.preventDefault();

    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    navigate("/", { state: { scrollTo: id } });

    setTimeout(() => {
      const nextElement = document.getElementById(id);
      if (nextElement) {
        nextElement.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }, 250);
  };

  const handleInternalNavigate = (event, path) => {
    event.preventDefault();
    navigate(path);
  };

  return (
    <footer className="bg-slate-100 text-slate-700 dark:bg-slate-950 dark:text-slate-300">
      <div className="border-t border-slate-200 dark:border-white/10">
        <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
          <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr_0.8fr_0.8fr]">
            <div className="rounded-[2rem] border border-slate-200 bg-white p-5 shadow-sm dark:border-white/10 dark:bg-slate-900">
              <div className="overflow-hidden rounded-[1.5rem] border border-slate-200 bg-slate-100 dark:border-white/10 dark:bg-slate-950">
                {mapUrl ? (
                  <iframe
                    title={`Localisation ${settings.nom_asso}`}
                    src={mapUrl}
                    width="100%"
                    height="230"
                    style={{ border: 0 }}
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    className="block"
                  />
                ) : (
                  <div className="flex h-[230px] items-center justify-center p-6 text-center text-sm text-slate-500 dark:text-slate-400">
                    Adresse non renseignée.
                  </div>
                )}
              </div>

              <div className="mt-5 space-y-3 text-sm">
                {fullAddress && (
                  <ContactLine icon={FiMapPin}>
                    <span>{fullAddress}</span>
                  </ContactLine>
                )}

                {settings.mail_asso && (
                  <ContactLine icon={FiMail}>
                    <a
                      href={`mailto:${settings.mail_asso}`}
                      className="transition hover:text-primary"
                    >
                      {settings.mail_asso}
                    </a>
                  </ContactLine>
                )}

                {settings.tel_asso && (
                  <ContactLine icon={FiPhone}>
                    <a
                      href={`tel:${String(settings.tel_asso).replace(/\s/g, "")}`}
                      className="transition hover:text-primary"
                    >
                      {settings.tel_asso}
                    </a>
                  </ContactLine>
                )}
              </div>
            </div>

            <div className="flex flex-col items-start rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900">
              <div className="flex items-center gap-4">
                <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-3xl border border-slate-200 bg-white p-2 shadow-sm dark:border-white/10 dark:bg-slate-950">
                  {logoUrl ? (
                    <img
                      src={logoUrl}
                      alt={`Logo ${settings.nom_asso}`}
                      className="h-full w-full object-contain"
                    />
                  ) : (
                    <div className="h-full w-full rounded-2xl bg-gradient-to-br from-primary via-blue-600 to-emerald-500" />
                  )}
                </div>

                <div>
                  <p className="text-xl font-bold text-slate-950 dark:text-white">
                    {settings.nom_asso || "MonAppliAsso"}
                  </p>
                  <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                    Annuaire & vitrine des associations
                  </p>
                </div>
              </div>

              <p className="mt-5 leading-7 text-slate-600 dark:text-slate-300">
                Un portail clair pour valoriser les associations, les
                événements, les infrastructures et les informations utiles aux
                habitants.
              </p>

              {(settings.facebook_asso || settings.instagram_asso) && (
                <div className="mt-6 flex items-center gap-3">
                  {settings.facebook_asso && (
                    <SocialButton
                      label="Facebook"
                      onClick={() => openExternal(settings.facebook_asso)}
                    >
                      <FaFacebookF />
                    </SocialButton>
                  )}

                  {settings.instagram_asso && (
                    <SocialButton
                      label="Instagram"
                      onClick={() => openExternal(settings.instagram_asso)}
                    >
                      <FaInstagram />
                    </SocialButton>
                  )}
                </div>
              )}

              {view === "vitrine" && (
                <div className="mt-6 rounded-2xl border border-primary/20 bg-primary/10 p-4 text-sm font-semibold text-primary dark:bg-primary/15">
                  Solution clé en main pour les communes, collectivités et
                  structures omnisports.
                </div>
              )}
            </div>

            <FooterColumn title="Navigation">
              {QUICK_LINKS.map((link) => (
                <FooterAnchor
                  key={link.id}
                  href={`#${link.id}`}
                  onClick={(event) => handleAnchorNavigate(event, link.id)}
                >
                  {link.label}
                </FooterAnchor>
              ))}
            </FooterColumn>

            <FooterColumn title="Informations">
              {LEGAL_LINKS.map((link) => (
                <FooterAnchor
                  key={link.path}
                  href={link.path}
                  onClick={(event) => handleInternalNavigate(event, link.path)}
                >
                  {link.label}
                </FooterAnchor>
              ))}

              <a
                href="https://www.monappliclub.com"
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:border-primary hover:text-primary dark:border-white/10 dark:bg-slate-950 dark:text-slate-200"
              >
                <FiExternalLink />
                MonAppliClub
              </a>
            </FooterColumn>
          </div>
        </div>
      </div>

      <div className="border-t border-slate-200 bg-white dark:border-white/10 dark:bg-slate-950">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-3 px-4 py-4 text-center text-xs text-slate-500 dark:text-slate-400 sm:px-6 md:flex-row lg:px-8">
          <p>
            Copyright © {new Date().getFullYear()} — Tous droits réservés.
          </p>

          <p className="inline-flex items-center gap-2">
            <FiShield className="text-primary" />
            Application développée par{" "}
            <a
              href="https://www.laurent-victorin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="font-bold text-slate-700 transition hover:text-primary dark:text-slate-200"
            >
              Laurent VICTORIN
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

function ContactLine({ icon: Icon, children }) {
  return (
    <p className="flex items-start gap-3">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Icon />
      </span>
      <span className="min-w-0 leading-7">{children}</span>
    </p>
  );
}

function SocialButton({ label, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-100 text-slate-700 transition hover:bg-primary hover:text-white dark:bg-white/10 dark:text-slate-200 dark:hover:bg-primary dark:hover:text-white"
    >
      {children}
    </button>
  );
}

function FooterColumn({ title, children }) {
  return (
    <div className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm dark:border-white/10 dark:bg-slate-900">
      <h3 className="text-sm font-bold uppercase tracking-[0.18em] text-slate-400">
        {title}
      </h3>

      <nav className="mt-5 flex flex-col gap-3">{children}</nav>
    </div>
  );
}

function FooterAnchor({ href, onClick, children }) {
  return (
    <a
      href={href}
      onClick={onClick}
      className="text-sm font-semibold text-slate-600 transition hover:translate-x-1 hover:text-primary dark:text-slate-300"
    >
      {children}
    </a>
  );
}